
import { useAuthStore } from '@/stores/authStore';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useAuthStore();

  const stats = [
    { name: 'Total Orders', value: '1,234', change: '+12%', icon: 'ri-shopping-cart-line', color: 'text-blue-600' },
    { name: 'Revenue', value: '$89,432', change: '+8%', icon: 'ri-money-dollar-circle-line', color: 'text-green-600' },
    { name: 'Products', value: '456', change: '+3%', icon: 'ri-package-line', color: 'text-purple-600' },
    { name: 'Customers', value: '789', change: '+15%', icon: 'ri-user-line', color: 'text-orange-600' },
  ];

  const recentOrders = [
    { id: 'ORD-001', customer: 'Sarah Johnson', product: 'Diamond Engagement Ring', status: 'In Progress', amount: '$2,500' },
    { id: 'ORD-002', customer: 'Michael Chen', product: 'Gold Necklace', status: 'Quality Check', amount: '$850' },
    { id: 'ORD-003', customer: 'Emily Davis', product: 'Pearl Earrings', status: 'Completed', amount: '$320' },
    { id: 'ORD-004', customer: 'David Wilson', product: 'Custom Wedding Band', status: 'Design Phase', amount: '$1,200' },
  ];

  const getQuickActions = () => {
    switch (user?.role) {
      case 'admin':
      case 'manager':
        return [
          { name: 'New Order', path: '/billing/invoices', icon: 'ri-add-box-line' },
          { name: 'Add Customer', path: '/customer/list', icon: 'ri-user-add-line' },
          { name: 'View Reports', path: '/reports/finance', icon: 'ri-bar-chart-line' },
          { name: 'Inventory', path: '/inventory', icon: 'ri-archive-line' },
        ];
      case 'sales':
        return [
          { name: 'New Order', path: '/billing/invoices', icon: 'ri-add-box-line' },
          { name: 'Add Customer', path: '/customer/list', icon: 'ri-user-add-line' },
          { name: 'Old Gold Purchase', path: '/inventory/old-gold', icon: 'ri-coins-line' },
          { name: 'Warranty Claims', path: '/warranty/claims', icon: 'ri-shield-check-line' },
        ];
      case 'qc':
        return [
          { name: 'Pending Inspections', path: '/qc/orders', icon: 'ri-search-eye-line' },
          { name: 'Manage Checklists', path: '/qc/checklists', icon: 'ri-task-line' },
          { name: 'Warranty Repairs', path: '/warranty/repairs', icon: 'ri-tools-line' },
          { name: 'QC Reports', path: '/qc', icon: 'ri-bar-chart-line' },
        ];
      case 'finance':
        return [
          { name: 'Create Invoice', path: '/billing/invoices', icon: 'ri-bill-line' },
          { name: 'Rate Fixation', path: '/billing/rates', icon: 'ri-price-tag-3-line' },
          { name: 'Finance Reports', path: '/reports/finance', icon: 'ri-file-chart-line' },
          { name: 'GST Reports', path: '/reports/gst', icon: 'ri-file-text-line' },
        ];
      case 'customer':
        return [
          { name: 'My Orders', path: '/customer/orders', icon: 'ri-shopping-bag-line' },
          { name: 'Warranty Claims', path: '/customer/warranty', icon: 'ri-shield-check-line' },
          { name: 'Update Profile', path: '/customer/profile', icon: 'ri-user-settings-line' },
          { name: 'Order History', path: '/customer/orders', icon: 'ri-history-line' },
        ];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.name}!
        </h1>
        <div className="text-sm text-gray-500">
          Role: {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 flex items-center justify-center ${stat.color}`}>
                  <i className={`${stat.icon} text-xl`}></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                    <i className="ri-trending-up-line text-sm mr-1"></i>
                    {stat.change}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
            <Link to="/billing/invoices" className="text-sm text-red-800 hover:text-red-900 cursor-pointer whitespace-nowrap">
              View all
            </Link>
          </div>
          <div className="space-y-4">
            {recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{order.id}</p>
                  <p className="text-sm text-gray-600">{order.customer}</p>
                  <p className="text-sm text-gray-500">{order.product}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{order.amount}</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    order.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    order.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'Quality Check' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            {getQuickActions().map((action) => (
              <Link
                key={action.name}
                to={action.path}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-6 h-6 flex items-center justify-center mx-auto mb-2">
                  <i className={`${action.icon} text-xl text-red-800`}></i>
                </div>
                <p className="text-sm font-medium text-center">{action.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
